import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const CACHE_KEY = "daton_public_branding";

// Hostnames that are NEVER a workspace subdomain (app hosts themselves)
const RESERVED_HOSTS = ["www", "app", "api", "admin", "dashboard", "portal"];

// Suffix patterns where `{slug}.{suffix}` means "slug" is a workspace
// Extend with your production domains.
const WORKSPACE_HOST_SUFFIXES = [
  ".daton.app",
  ".daton.local",
  ".daton.cloud",
  ".localhost",
];

/** Parse hostname → workspace slug, or null. */
export function resolveSlugFromHost(hostname) {
  if (!hostname) return null;
  const lower = hostname.toLowerCase();
  // Direct IP or single-segment → nothing to extract
  if (/^\d+\.\d+\.\d+\.\d+$/.test(lower)) return null;
  if (!lower.includes(".")) return null;
  // Must end with a whitelisted suffix
  const match = WORKSPACE_HOST_SUFFIXES.find(s => lower.endsWith(s));
  if (!match) return null;
  const prefix = lower.slice(0, lower.length - match.length);
  if (!prefix || prefix.includes(".")) return null; // only single-level subdomain
  if (RESERVED_HOSTS.includes(prefix)) return null;
  // Slug rules (matches backend tenant slug validation)
  if (!/^[a-z0-9-]{2,40}$/.test(prefix)) return null;
  return prefix;
}

/**
 * useWorkspaceBranding(slug?)
 * Loads public branding:
 *   1. explicit slug argument (highest priority)
 *   2. subdomain detection (rminformatik.daton.app → 'rminformatik')
 *   3. URL ?workspace=xyz
 *   4. last-used slug from localStorage cache
 */
export function useWorkspaceBranding(explicitSlug) {
  const [branding, setBranding] = useState(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });

  useEffect(() => {
    let slug = explicitSlug;
    if (!slug) slug = resolveSlugFromHost(window.location.hostname);
    if (!slug) {
      try {
        const u = new URL(window.location.href);
        slug = u.searchParams.get("workspace") || null;
      } catch { /* ignore */ }
    }
    if (!slug) return; // keep cached

    (async () => {
      try {
        const r = await axios.get(`${API}/tenants/branding/public`, { params: { slug } });
        setBranding(r.data);
        localStorage.setItem(CACHE_KEY, JSON.stringify(r.data));
      } catch {
        setBranding(null);
        localStorage.removeItem(CACHE_KEY);
      }
    })();
  }, [explicitSlug]);

  return branding;
}

export function cachePublicBranding(b) {
  if (b) localStorage.setItem(CACHE_KEY, JSON.stringify(b));
  else localStorage.removeItem(CACHE_KEY);
}
